// VideoPlayer.tsx
import React, {
  forwardRef,
  type ReactNode,
  useRef,
  useState,
  useCallback,
  useMemo,
  useImperativeHandle,
} from "react";
import { Alert, Box, Snackbar } from "@mui/material";
import PlayerWhep, { type PlayerWhepHandle } from "./PlayerWhep";
import { playerSurfaceColors } from "../theme";


export type IceServers = string | RTCIceServer[];

export type VideoPlayerProps = {
  // --- WHEP / stream identity ---
  webRtcServerUrl: string;   // e.g. https://your-host/whep
  platformName: string;      // platform (uav) name/id
  sensorName: string;        // sensor (channel) name/id

  // --- Playback / UX ---
  showControls?: boolean;            // show builtin controls
  iceServers?: IceServers;           // TURN/STUN config
  lowLatencyHints?: boolean;         // shrink jitter buffer, real-time bias
  receiverBufferMs?: number;         // bounded playout reservoir for bursty file sources
  fastDrainOnStart?: boolean;        // speed up briefly to drain startup buffer
  preferLowLatencyCodecs?: boolean;  // favor VP8/H264 baseline
  overlay?: ReactNode;
  header?: ReactNode;
  maxHeight?: number;
  highlighted?: boolean;
  surfaceRef?: (node: HTMLDivElement | null) => void;

  // --- Callbacks ---
  onDataReceive?: (packet: { time: Date; klv: unknown }) => void;
  onResolutionDetect?: (w: number, h: number) => void;
  onDimensionsChange?: (w: number, h: number) => void;
  onStateChange?: (state: string) => void;
  onSensorMessage?: (msg: unknown) => void;
  onSensorError?: (err: unknown) => void;
  onError?: (err: unknown) => void;
};

export type VideoPlayerRef = {
  platformName: string;
  sensorName: string;
  getSnapshot?: () => HTMLCanvasElement | null;
  inner?: unknown; // underlying StanagPlayerWhep ref
};

const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(
  (props, ref) => {
    const {
      webRtcServerUrl,
      platformName,
      sensorName,
      iceServers,
      showControls = true,
      lowLatencyHints = true,
      receiverBufferMs = 0,
      fastDrainOnStart = true,
      preferLowLatencyCodecs = true,
      overlay,
      header,
      maxHeight,
      highlighted = false,
      surfaceRef,
      onDataReceive,
      onResolutionDetect,
      onDimensionsChange,
      onStateChange,
      onSensorMessage,
      onSensorError,
      onError,
    } = props;

    const playerRef = useRef<PlayerWhepHandle | null>(null);
    const overlayRootRef = useRef<HTMLDivElement | null>(null);
    const [snackbar, setSnackbar] = useState<{
      open: boolean;
      message: string;
      severity: "success" | "error";
    } | null>(null);
    const [aspectRatio, setAspectRatio] = useState<number>(16 / 9);

    const composeOverlaySnapshot = useCallback((baseCanvas: HTMLCanvasElement) => {
      const overlayRoot = overlayRootRef.current;
      if (!overlayRoot) {
        return baseCanvas;
      }

      const overlayRect = overlayRoot.getBoundingClientRect();
      if (overlayRect.width < 1 || overlayRect.height < 1) {
        return baseCanvas;
      }

      const overlayCanvases = Array.from(overlayRoot.querySelectorAll("canvas"));
      if (overlayCanvases.length === 0) {
        return baseCanvas;
      }

      const compositeCanvas = document.createElement("canvas");
      compositeCanvas.width = baseCanvas.width;
      compositeCanvas.height = baseCanvas.height;

      const compositeContext = compositeCanvas.getContext("2d");
      if (!compositeContext) {
        return baseCanvas;
      }

      compositeContext.drawImage(baseCanvas, 0, 0);

      const scaleX = compositeCanvas.width / overlayRect.width;
      const scaleY = compositeCanvas.height / overlayRect.height;

      for (const overlayCanvas of overlayCanvases) {
        const rect = overlayCanvas.getBoundingClientRect();
        if (rect.width < 1 || rect.height < 1) {
          continue;
        }

        const drawX = (rect.left - overlayRect.left) * scaleX;
        const drawY = (rect.top - overlayRect.top) * scaleY;
        const drawWidth = rect.width * scaleX;
        const drawHeight = rect.height * scaleY;

        compositeContext.drawImage(overlayCanvas, drawX, drawY, drawWidth, drawHeight);
      }

      return compositeCanvas;
    }, []);


    // Normalize callbacks to old shapes
    const handleDataReceived = useCallback(
      (time: number, klvs: unknown) => {
        // Convert MISB601 timestamp (milliseconds since epoch) to Date (ms precision)
        const date = new Date(time); // JS Date preserves ms
        onDataReceive?.({ time: date, klv: klvs });
      },
      [onDataReceive]
    );

    const handleDimensionsChange = useCallback(
      ({ width: w, height: h }: { width: number; height: number }) => {
        if (w > 0 && h > 0) {
          setAspectRatio(w / h);
        }
        onDimensionsChange?.(w, h);
      },
      [onDimensionsChange]
    );

    const handleStreamInfo = useCallback(
      ({ videoWidth, videoHeight }: { videoWidth: number; videoHeight: number }) => {
        if (videoWidth > 0 && videoHeight > 0) {
          setAspectRatio(videoWidth / videoHeight);
        }
        onResolutionDetect?.(videoWidth, videoHeight);
      },
      [onResolutionDetect]
    );

    const handleStateChange = useCallback(
      (state: string) => onStateChange?.(state),
      [onStateChange]
    );

    const handleSensorMessage = useCallback(
      (msg: unknown) => onSensorMessage?.(msg),
      [onSensorMessage]
    );

    const handleSensorError = useCallback(
      (err: unknown) => onSensorError?.(err),
      [onSensorError]
    );

    const handleError = useCallback(
      (err: unknown) => {
        const message =
          err instanceof Error ? err.message : typeof err === "string" ? err : String(err);
        setSnackbar({
          open: true,
          message,
          severity: "error",
        });
        onError?.(err);
      },
      [onError]
    );


    useImperativeHandle(
      ref,
      () => ({
        platformName,
        sensorName,
        getSnapshot: () => {
          const baseCanvas = playerRef.current?.getSnapshot?.() ?? null;
          if (!baseCanvas) {
            return null;
          }

          return composeOverlaySnapshot(baseCanvas);
        },
        inner: playerRef.current ?? undefined,
      }),
      [composeOverlaySnapshot, platformName, sensorName]
    );

    const player = useMemo(() => {
      if (!webRtcServerUrl || !platformName || !sensorName) {
        return null;
      }

      return (
        <PlayerWhep
          key={`${webRtcServerUrl}::${platformName}::${sensorName}`}
          ref={playerRef}
          serverUrl={webRtcServerUrl}
          platform={platformName}
          sensor={sensorName}
          iceServers={iceServers}
          useStandardControls
          controls={!!showControls}
          showErrors={false}
          lowLatencyHints={lowLatencyHints}
          receiverBufferMs={receiverBufferMs}
          fastDrainOnStart={fastDrainOnStart}
          preferLowLatencyCodecs={preferLowLatencyCodecs}
          layer={"main"}
          simulcastEnabled={false}
          onDataReceive={handleDataReceived}
          onSizeChange={handleDimensionsChange}
          onStreamInfo={handleStreamInfo}
          onStateChange={handleStateChange}
          onSensorMessage={handleSensorMessage}
          onSensorError={handleSensorError}
          onError={handleError}
        />
      );
    },
      [
        webRtcServerUrl,
        platformName,
        sensorName,
        iceServers,
        showControls,
        lowLatencyHints,
        receiverBufferMs,
        fastDrainOnStart,
        preferLowLatencyCodecs,
        handleDataReceived,
        handleDimensionsChange,
        handleStreamInfo,
        handleStateChange,
        handleSensorMessage,
        handleSensorError,
        handleError,
      ]
    );

    return (
      <Box sx={{ width: "100%", maxWidth: maxHeight === undefined ? undefined : maxHeight * aspectRatio }}>
        {header}
        <Box
          ref={surfaceRef}
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio,
            bgcolor: (theme) => playerSurfaceColors[theme.palette.mode],
            borderRadius: "10px",
            outline: highlighted ? "2px solid" : undefined,
            outlineColor: "primary.main",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{ width: "100%", height: "100%" }}>
            {player ?? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                  fontSize: 14,
                  px: 2,
                  textAlign: "center",
                }}
              >
                Preparing stream...
              </Box>
            )}
          </Box>
          {overlay ? (
            <Box
              ref={overlayRootRef}
              sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
              }}
            >
              {overlay}
            </Box>
          ) : null}
          <Snackbar
            open={Boolean(snackbar?.open)}
            autoHideDuration={4000}
            onClose={() => setSnackbar(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert severity={snackbar?.severity ?? "success"}>
              {snackbar?.message ?? ""}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    );
  }
);

VideoPlayer.displayName = "VideoPlayer";

export default VideoPlayer;
